import type { FC, ReactElement } from 'react';
import { Dropdown } from 'antd';
import { TbCircleCheck, TbUser, TbUserCheck, TbUsers } from 'react-icons/tb';

import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
} from '@/shared/components/dropdowns';
import { LearnMoreFooter } from '@/shared/components/layouts/footers';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import type { TaskAssignee } from '@/types';

import LifecycleTaskList from '../LifecycleTaskList';
import type { LifecycleTaskPreview } from '../types';

interface LifecycleSummaryDropdownProps {
    children: ReactElement;
    placement?: 'bottomLeft' | 'bottomRight';
}

interface LifecycleTaskSummaryDropdownProps extends LifecycleSummaryDropdownProps {
    completed?: boolean;
    tasks: LifecycleTaskPreview[];
}

interface LifecycleMemberSummaryDropdownProps extends LifecycleSummaryDropdownProps {
    participants: TaskAssignee[];
    variant: 'assignees' | 'reviewers';
}

const dropdownTrigger = (children: ReactElement) => (
    <span style={{ cursor: 'help', display: 'inline-flex' }}>{children}</span>
);

const LifecycleTaskSummaryDropdown: FC<LifecycleTaskSummaryDropdownProps> = ({
    children,
    completed = false,
    placement = 'bottomLeft',
    tasks,
}) => {
    const label = completed ? 'Completed Tasks' : 'Lifecycle Tasks';

    return (
        <Dropdown
            destroyOnHidden
            menu={{ items: [] }}
            placement={placement}
            popupRender={() => (
                <DropdownPanel
                    contentStyle={{ padding: 0 }}
                    footer={<LearnMoreFooter topic='lifecycle tasks and progress' variant='dropdown' />}
                    header={
                        <DropdownMenuLabel
                            icon={
                                completed ? (
                                    <TbCircleCheck size='0.875rem' />
                                ) : (
                                    <WorkspaceResourceGlyph resource='task' size='0.875rem' />
                                )
                            }
                        >
                            {label}
                        </DropdownMenuLabel>
                    }
                    maxHeight='20rem'
                    width='23rem'
                >
                    <LifecycleTaskList
                        emptyDescription={
                            completed
                                ? 'Completed tasks will appear here as work progresses.'
                                : 'Tasks will appear here when they are added to this lifecycle.'
                        }
                        emptyHeight='9rem'
                        emptyLabel={completed ? 'No Completed Tasks' : 'No Lifecycle Tasks'}
                        tasks={tasks}
                    />
                </DropdownPanel>
            )}
            trigger={['hover']}
        >
            {dropdownTrigger(children)}
        </Dropdown>
    );
};

const participantName = (participant: TaskAssignee) => {
    const { user } = participant.member;

    return [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.emailAddress || 'Workspace Member';
};

const LifecycleMemberSummaryDropdown: FC<LifecycleMemberSummaryDropdownProps> = ({
    children,
    participants,
    placement = 'bottomLeft',
    variant,
}) => {
    const assignees = variant === 'assignees';
    const label = assignees ? 'Lifecycle Assignees' : 'Lifecycle Reviewers';
    const Icon = assignees ? TbUsers : TbUserCheck;

    return (
        <Dropdown
            destroyOnHidden
            menu={{ items: [] }}
            placement={placement}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='lifecycle participants and permissions' variant='dropdown' />}
                    header={<DropdownMenuLabel icon={<Icon size='0.875rem' />}>{label}</DropdownMenuLabel>}
                    maxHeight='18rem'
                    width='19rem'
                >
                    {participants.length ? (
                        <DropdownPanelMenu
                            items={participants.map((participant) => {
                                const name = participantName(participant);
                                const { user } = participant.member;

                                return {
                                    key: participant.id,
                                    label: (
                                        <DropdownOptionContent
                                            avatar={{
                                                icon: <TbUser />,
                                                props: { children: name.slice(0, 1), shape: 'circle', size: 24 },
                                                src: user?.avatar,
                                            }}
                                            metadata={user?.emailAddress ? [user.emailAddress] : undefined}
                                            title={name}
                                        />
                                    ),
                                };
                            })}
                            selectable={false}
                        />
                    ) : (
                        <EmptyView
                            description={`Lifecycle ${variant} will appear here when they are assigned.`}
                            height='9rem'
                            icon={<Icon size='1rem' />}
                            label={`No Lifecycle ${assignees ? 'Assignees' : 'Reviewers'}`}
                        />
                    )}
                </DropdownPanel>
            )}
            trigger={['hover']}
        >
            {dropdownTrigger(children)}
        </Dropdown>
    );
};

export { LifecycleMemberSummaryDropdown, LifecycleTaskSummaryDropdown };
